import type { ReactNode } from "react";

import type { PaginationProps } from "./Table";

// show max 7 page numbers, then an ellipsis, then the last 2 pages
const getPageNumbers = (current: number, total: number) => {
  const pages: (number | string)[] = [];
  const maxVisible = 7;

  if (total <= maxVisible) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  // always show first page
  pages.push(1);

  if (current <= 3) {
    // show first few pages
    pages.push(2, 3, 4, "...", total);
  } else if (current >= total - 2) {
    // show last few pages
    pages.push("...", total - 3, total - 2, total - 1, total);
  } else {
    // show pages around current
    pages.push("...", current - 1, current, current + 1, "...", total);
  }

  return pages;
};

type PaginationControlsProps = PaginationProps & {
  leftContent?: ReactNode;
};

export const PaginationControls = ({
  pageSize,
  pageIndex,
  rowCount,
  onPageChange,
  isLoading,
  reportMeta,
  reportType,
  leftContent,
}: PaginationControlsProps) => {
  const totalPages = Math.ceil(rowCount / pageSize);

  return (
    <div className="table-status-bar">
      <div className="status-section status-section--left">{leftContent}</div>
      <div className="status-section status-section--center">
        <button
          className="nav-button"
          onClick={() => onPageChange(pageIndex - 1)}
          disabled={pageIndex === 1 || isLoading}
        >
          <span className="material-icons">chevron_left</span>
          Previous
        </button>
        <div className="page-numbers">
          {getPageNumbers(pageIndex, totalPages).map((page, idx) =>
            page === "..." ? (
              <span key={`ellipsis-${idx}`} className="ellipsis">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page as number)}
                className={pageIndex === page ? "active" : ""}
                disabled={isLoading}
              >
                {page}
              </button>
            ),
          )}
        </div>
        <button
          className="nav-button"
          onClick={() => onPageChange(pageIndex + 1)}
          disabled={pageIndex >= totalPages || isLoading}
        >
          Next
          <span className="material-icons">chevron_right</span>
        </button>
      </div>
      <div className="status-section status-section--right">
        {(() => {
          const showingStart = (pageIndex - 1) * pageSize + 1;
          const showingEnd = Math.min(pageIndex * pageSize, rowCount);

          // For WORK_IN_PROGRESS reports, include file count if available
          if (reportType === "WORK_IN_PROGRESS" && reportMeta?.total_files) {
            const fileCount = Number(reportMeta.total_files);
            return `Showing ${showingStart} to ${showingEnd} of ${rowCount} results from ${fileCount} files`;
          }

          // Default format for other reports
          return `Showing ${showingStart} to ${showingEnd} of ${rowCount} results`;
        })()}
      </div>
    </div>
  );
};
